import { type NotificationCategory, type UserNotificationSettings } from '@/models/user';

export type SelectionState = 'selected' | 'unselected' | 'indeterminate';

export type NotificationChannel = 'email' | 'in_app';

export const EMAIL_ONLY_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  'non_digitalized_workshop_cc',
];

type ChannelSelectionState = { [K in NotificationChannel]: SelectionState };
type ChannelSelectionStateByCategory = { [K in NotificationCategory]: ChannelSelectionState };
type ChannelSelectionStateAll = {
  all: ChannelSelectionState;
} & ChannelSelectionStateByCategory;

function getSelectionStateForChannel(states: SelectionState[]): SelectionState {
  const selected = states.filter((s) => s === 'selected').length;
  const unselected = states.filter((s) => s === 'unselected').length;

  if (selected === states.length) return 'selected';
  if (unselected === states.length) return 'unselected';

  return 'indeterminate';
}

/**
 * For each notification category and channel (email, in_app), return if the channel is selected,
 * unselected or indeterminate (for "all" row when categories are partially selected)
 */
export const getChannelSelectionStateByCategory = (
  userNotificationSettings: UserNotificationSettings
): ChannelSelectionStateAll => {
  const channelStateForCategory = (channels: ('in_app' | 'email')[]): ChannelSelectionState => ({
    email: channels.includes('email') ? 'selected' : 'unselected',
    in_app: channels.includes('in_app') ? 'selected' : 'unselected',
  });

  const byCategory: ChannelSelectionStateByCategory = {
    new_tasks: channelStateForCategory(userNotificationSettings.new_tasks),
    task_reminders: channelStateForCategory(userNotificationSettings.task_reminders),
    task_alerts: channelStateForCategory(userNotificationSettings.task_alerts),
    new_comments: channelStateForCategory(userNotificationSettings.new_comments),
    new_assignations: channelStateForCategory(userNotificationSettings.new_assignations),
    new_events: channelStateForCategory(userNotificationSettings.new_events),
    non_digitalized_workshop_cc: channelStateForCategory(
      userNotificationSettings.non_digitalized_workshop_cc
    ),
  };

  const categories = Object.keys(byCategory) as NotificationCategory[];
  const inAppCategories = categories.filter(
    (category) => !EMAIL_ONLY_NOTIFICATION_CATEGORIES.includes(category)
  );

  const all: ChannelSelectionState = {
    email: getSelectionStateForChannel(Object.values(byCategory).map((c) => c.email)),
    in_app: getSelectionStateForChannel(
      inAppCategories.map((category) => byCategory[category].in_app)
    ),
  };

  return { all, ...byCategory };
};
